<script lang="ts">
  import Icon from "../icons/Icon.svelte";
  import SubmitIconButton from "./SubmitEmailButton.svelte";
  import Button from "../form/Button.svelte";
  import Link from "../link/Link.svelte";
  import FormInput from "../form/FormInputWithSurroundError.svelte";
  import checkMobileDevice from "../is-mobile";
  import { createForm } from "felte";
  import * as yup from "yup";
  import { validator } from "@felte/validator-yup";
  import { addEmailToWaitlist } from "../../../app/api";
  import { onMount } from "svelte";

  /**
   * Indicate if icon or text button will be used.
   */
  export let useIconButton: boolean = true;
  /**
   * Indicate if input and button should be on row (horizontal)
   * or on column (vertical)
   */
  export let orientation: "horizontal" | "vertical" = "horizontal";
  /**
   * Indicate the waitlist element is in focus (input or button)
   */
  export let isWaitlistFocus: boolean = false;
  /**
   * Indicate if need to use box-shadow around the input
   */
  export let inputBoxShadow: boolean = false;
  /**
   * Indicate if need to use box-shadow around the success section
   */
  export let successBoxShadow: boolean = false;
  /**
   * Indicate if the user has submitted their email
   */
  export let isSubmitted: boolean = false;

  /**
   * A callback when user email has been added to waitlist
   */
  export let submittedSuccessCallback = () => {};

  /**
   * When the email has been successfully added, we show the message
   * It controls that the size of success should be larger or equal
   * to the input
   */
  export let increaseSizeOnSuccess: boolean = false;

  export let hasInputBorder: boolean = false;

  const isMobile = checkMobileDevice();
  const serverErrorMessage = "Something went wrong. Please try again.";
  const invalidEmailErrorMessage = "Invalid email. Please try again.";
  let isInputFocus: boolean = false; // input focus
  let isButtonFocus: boolean = false; // button focus
  let value: string = "";
  let success: boolean = false;
  let isSubmitting: boolean = false;
  let errorMessage: string;
  let buttonElement: HTMLElement;
  let waitlistElement: HTMLElement;

  const schema = yup.object({
    email: yup.string().email().required(),
  });

  const { form, errors, validate } = createForm({
    extend: validator({ schema }),
    onSubmit: async (values) => {
      if (isSubmitting) return;
      isSubmitting = true;
      const email = values.email;
      await validate();
      updateErrorMessage();

      if (!errorMessage) {
        const res = await addEmailToWaitlist(email, {
          onError: async () => {
            success = false;
            isSubmitting = false;
            errorMessage = serverErrorMessage;
          },
        });

        if (res) {
          // console.log("Add email to Waitlist: ", res.data);
          if (res.status === 200) {
            success = true;
            errorMessage = "";
            submittedSuccessCallback();
          } else {
            success = false;
            errorMessage =
              res.status === 400
                ? invalidEmailErrorMessage
                : serverErrorMessage;
          }
        }
      }

      isSubmitting = false;
    },
  });

  function updateErrorMessage() {
    let errs = $errors;
    if (!errs) return;
    errorMessage = errs.email ? invalidEmailErrorMessage : "";
  }

  function onButtonFocus() {
    isButtonFocus = true;
  }

  function onButtonBlur() {
    isButtonFocus = false;
  }

  onMount(() => {
    buttonElement = waitlistElement.querySelector(".submit-button button");
    if (buttonElement) {
      buttonElement.addEventListener("focus", onButtonFocus);
      buttonElement.addEventListener("blur", onButtonBlur);
    }
    return () => {
      if (buttonElement) {
        buttonElement.removeEventListener("focus", onButtonFocus);
        buttonElement.removeEventListener("blur", onButtonBlur);
      }
    };
  });

  async function handleFormSubmit() {
    await validate();
    updateErrorMessage();
  }

  // $: $errors, updateErrorMessage();
  $: success = isSubmitted;
  $: isWaitlistFocus = isInputFocus || isButtonFocus || (!!value && !success);
</script>

<div
  bind:this={waitlistElement}
  class="waitlist-container"
  class:bigger-size={success && increaseSizeOnSuccess}
  class:mobile={isMobile}
  class:active={isInputFocus}
  class:has-border={hasInputBorder}
  class:vertical={orientation === "vertical"}
>
  <form use:form class:opacity-0={success} on:submit|capture={handleFormSubmit}>
    <div class="email-container">
      <div class="input-email relative flex-grow">
        <div class="prefix-icon">
          <Icon icon="input-email" width="20px" height="18px" />
        </div>

        <FormInput
          bind:value
          bind:isFocus={isInputFocus}
          name="email"
          {errorMessage}
          hasActiveStyle={false}
          errorIndicatorIconSize="20px"
          type="text"
          spellcheck="false"
          placeholder="Enter Your Email"
          autocomplete="off"
          disabled={isSubmitting}
          style={inputBoxShadow
            ? "box-shadow: 0px 5px 20px rgba(55, 84, 170, 0.2);"
            : ""}
        />
      </div>

      <div class="submit-button">
        {#if useIconButton}
          <SubmitIconButton {isMobile} isLoading={isSubmitting} />
        {:else}
          <Button
            disabled={isSubmitting}
            style="height: 48px; font-size: 11px; font-weight: 600;"
          >
            JOIN WAITLIST
          </Button>
        {/if}
      </div>
    </div>
  </form>

  {#if success}
    <div class="success-indicator" class:success-shadow={successBoxShadow}>
      <div class="success-icon">
        <Icon icon="success-indicator" size={isMobile ? "28px" : "36px"} />
      </div>

      <div class="success-text">
        <p>Your email has been submitted.</p>
        <p>
          We’d love to get your feedback on our
          <Link href="https://forms.gle/r3H4XVwUNKQM2ovq5" openInNewTab>
            brief questionnaire
          </Link>
          if you have a minute.
        </p>
      </div>
    </div>
  {/if}
</div>

<style lang="postcss">
  .waitlist-container {
    @apply block relative h-full mx-4 z-10;
    width: calc(100% - 32px);
    font-size: 15px;
  }

  .waitlist-container.bigger-size {
    @apply pb-12;
  }

  .waitlist-container.bigger-size.mobile {
    @apply pb-2;
  }

  .waitlist-container.mobile {
    font-size: 13px;
  }

  .email-container {
    @apply relative w-full flex flex-row items-start space-x-1.5;
  }

  .waitlist-container.vertical .email-container {
    @apply flex flex-col space-y-8 w-full;
  }

  .waitlist-container.vertical .input-email,
  .waitlist-container.vertical .submit-button,
  .waitlist-container.vertical .submit-button :global(button) {
    @apply w-full;
  }

  .waitlist-container.vertical .submit-button {
    @apply m-0;
  }

  .waitlist-container.vertical .submit-button :global(button) {
    height: 40px !important;
  }

  .email-container :global(input) {
    @apply w-full h-12 font-medium rounded outline-none;
    padding: 12px 40px 12px 50px;
    font-size: clamp(0.8125rem, 0.5rem + 0.5vw, 0.9375rem);
    color: #0e0121;
  }

  .waitlist-container:not(.has-border) :global(input) {
    @apply border-none;
  }

  .email-container :global(input::placeholder) {
    color: rgba(167, 176, 181, 0.8);
    font-weight: 300;
  }

  .email-container .prefix-icon {
    @apply absolute left-4 top-0 bottom-0 z-10 flex flex-row items-center;
    color: #d3dbe0;
  }

  .waitlist-container.active .prefix-icon {
    color: #a7b0b5;
  }

  .submit-button {
    @apply flex flex-row items-center top-0 bottom-0 right-1.5 flex-grow-0 flex-shrink-0;
  }

  /* success indicator */
  .success-indicator {
    @apply absolute flex flex-row items-center w-full bg-white;
    /* height: 48px; */
    border-radius: 6px;
    top: 0px;
    left: 0px;
    right: 0px;
  }

  .success-icon {
    @apply pl-6 flex flex-row items-center justify-center;
  }

  .mobile .success-icon {
    @apply pl-3;
  }

  .success-text {
    @apply font-medium;
    color: #454450;
    opacity: 0.8;
    padding: 20px 32px 20px 20px;
    font-size: 13px;
  }

  .success-shadow {
    box-shadow: 0px 5px 20px rgba(55, 84, 170, 0.2);
  }

  .mobile .success-text {
    padding: 10px 16px 10px 10px;
    font-size: 11px;
  }

  .waitlist-container :global(.error-message) {
    font-size: 11px;
  }
</style>
